import React, { useEffect, useState } from 'react';

import CloseIcon from '@mui/icons-material/Close';
import {
  Fade, Grid, Modal as MUIModal, Skeleton, Stack, Typography,
} from '@mui/material';
import _ from 'lodash';

import {
  BaseColorKeys, NotifierColorKeys, SubjectColorKeys,
} from '../../theme';
import { UserType } from '../../types/common';
import CollapsableNotification from '../CollapsableNotification';
import ProfileIcon from '../ProfileIcon';
import { ColorHex } from '../ProfileIcon/types';
import SearchField from '../SearchField';
import UserListItem from '../UserListItem';

import {
  ActionButton,
  ActionContainer,
  CloseIconButton,
  Container,
  DetailDivider,
  EmptyMessageContainer,
  SearchContainer,
  SearchFieldContainer,
  ShareButton,
  TitleContainer,
  UnshareButton,
  UserDescription,
  UserIdentifierStack,
  UserName,
  UserPhoneNumber,
  UserRow,
  UsersList,
} from './index.styles';

const SkeletonContent = () => (
  <>
    <Stack spacing={1}>
      <Skeleton variant="rectangular" height={24} width={180} />
      <Skeleton variant="rectangular" height={12} width={360} />
    </Stack>
    <DetailDivider />
    <Stack spacing={1}>
      <Skeleton variant="rectangular" height={12} width={360} />
      <Skeleton variant="rectangular" height={12} width={360} />
      <Skeleton variant="rectangular" height={12} width={320} />
    </Stack>
  </>
);

type UserProperties = {
  firstName?: string;
  lastName?: string;
  profileIcon?: {
    color?: ColorHex;
    image: string;
  } | {
    color: ColorHex;
    image?: string;
  };
  phoneNumber?: string;
  isAccessEditable?: boolean;
  type?: UserType;
}

export type User = {
  id: string,
  name: string,
} & UserProperties;

interface ShareModalProps {
  open: boolean,
  handleClose: (event?: object, reason?: string) => void
  hideCloseIcon?: boolean,
  subject?: string,
  searchPlaceholder?: string,
  isSearchFetching?: boolean,
  searchUsers: User[],
  users: User[],
  loading?: boolean,
  error?: any,
  titleColor?: BaseColorKeys | SubjectColorKeys | NotifierColorKeys,
  search: (searchStr: string) => void,
  share: (id: string) => void,
  unshare: (id: string) => void,
  isUnshareLoading?: boolean,
  isShareLoading?: boolean,
}

const ShareModal = ({
  open = false,
  handleClose = () => {},
  hideCloseIcon = false,
  subject = '',
  searchPlaceholder = 'User',
  isSearchFetching = false,
  searchUsers,
  users = [],
  error,
  loading = false,
  titleColor = 'primary',
  search = () => {},
  share = () => {},
  unshare = () => {},
  isUnshareLoading = false,
  isShareLoading = false,
}: ShareModalProps) => {
  const [searchStr, setSearchStr] = useState<string>('');
  const [userId, setUserId] = useState<string | undefined>(undefined);

  const [errorOpen, setErrorOpen] = useState(!!error);
  // The ID for the currently loading unshare button is stored, assuming only one
  // unshare action is taken at a time
  const [unshareLoadingId, setUnshareLoadingId] = useState<string>('');

  useEffect(() => {
    if (searchStr === '') {
      setUserId(undefined);
    } else {
      search(searchStr);
    }
  }, [searchStr]);

  useEffect(() => {
    if (error) setErrorOpen(true);
    else setErrorOpen(false);
  }, [error]);

  useEffect(() => {
    if (!isUnshareLoading && !users.map((u) => u.id).includes(unshareLoadingId)) {
      // Simply checking for !isUnshareLoading results in the unshare button being visible
      // while the displayed list of users updates
      // The loading for the unshare button should stop when unshare is complete AND the
      // corresponding button has been removed from render
      setUnshareLoadingId('');
    }
  }, [isUnshareLoading, users]);

  const onShareButtonClick = () => {
    if (userId) {
      share(userId);
      setSearchStr('');
    }
  };

  return (
    <MUIModal
      open={open}
      onClose={handleClose}
      closeAfterTransition
    >
      <Fade in={open}>
        <Container>
          {loading ? <SkeletonContent /> : (
            <>
              <Grid container sx={{ alignItems: 'start', justifyContent: 'space-between' }}>
                <Grid item xs={11}>
                  <TitleContainer color={titleColor}>
                    <Typography
                      variant="h4"
                    >{subject ? `Share "${subject}"` : 'Share'}
                    </Typography>
                  </TitleContainer>
                </Grid>
                <Grid item xs="auto">
                  {!hideCloseIcon && (
                    <CloseIconButton onClick={handleClose}>
                      <CloseIcon />
                    </CloseIconButton>
                  )}
                </Grid>
              </Grid>
              <DetailDivider />
              <CollapsableNotification
                open={errorOpen}
                close={() => setErrorOpen(false)}
                message={error?.data?.message}
              />
              <SearchContainer>
                <SearchFieldContainer>
                  <SearchField
                    type="dropdown"
                    placeholder={`Filter ${searchPlaceholder}...`}
                    options={searchUsers || []}
                    getOptionKey={(x: User) => x?.id}
                    getOptionLabel={(x: User) => x?.name}
                    onSearch={(str) => setSearchStr(str)}
                    loading={isSearchFetching}
                    onSelect={(x: User) => setUserId(x?.id)}
                    clearOnSelect
                    renderOption={(props, x) => (
                      <UserListItem
                        {...props}
                        firstName={x.firstName}
                        lastName={x.lastName}
                        profileIconColor={x.profileIcon?.color}
                        profileIconImage={x.profileIcon?.image}
                        displayedProprties={[x.phoneNumber]}
                        key={x.id}
                      />
                    )}
                  />
                </SearchFieldContainer>
                <ShareButton
                  loading={isShareLoading}
                  size="medium"
                  color="darkPrimary"
                  variant="contained"
                  disabled={userId === undefined}
                  onClick={onShareButtonClick}
                >
                  Share
                </ShareButton>
              </SearchContainer>
              <UsersList>
                {users.length === 0 && (
                  <EmptyMessageContainer>
                    <Typography variant="overline">
                      NO USER HAS ACCESS YET
                    </Typography>
                  </EmptyMessageContainer>
                )}
                {users.map((u) => (
                  <UserRow
                    key={u.id}
                    underline="none"
                    color="inherit"
                    href={`/admin/${u.type === 'STUDENT' ? 'student' : 'staff'}/${u.id}`}
                  >
                    <UserDescription>
                      <ProfileIcon
                        firstName={u?.firstName || u?.name.split(' ')[0]}
                        lastName={u?.lastName || _.last(u?.name.split(' '))}
                        color={u?.profileIcon?.color}
                        imageUrl={u?.profileIcon?.image}
                        size={32}
                      />
                      <UserIdentifierStack>
                        <UserName variant="body2">
                          {u.name}
                        </UserName>
                        <UserPhoneNumber variant="caption">
                          {u.phoneNumber}
                        </UserPhoneNumber>
                      </UserIdentifierStack>
                    </UserDescription>
                    <UnshareButton
                      size="small"
                      color="error"
                      variant="text"
                      allCaps
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        unshare(u.id);
                        setUnshareLoadingId(u.id);
                      }}
                      loading={unshareLoadingId === u.id}
                      disabled={!u.isAccessEditable}
                    >
                      Unshare
                    </UnshareButton>
                  </UserRow>
                ))}
              </UsersList>
              <ActionContainer>
                <ActionButton
                  size="medium"
                  color="darkPrimary"
                  onClick={handleClose}
                >
                  Done
                </ActionButton>
              </ActionContainer>
            </>
          )}
        </Container>
      </Fade>
    </MUIModal>
  );
};

export default ShareModal;
